import React, { memo, ReactElement, useContext, useEffect, useState } from 'react';
import { Point, TooltipOptions } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts.js';
import CustomEvents from 'highcharts-custom-events';
import HighchartsReact from 'highcharts-react-official';
import ReactDOMServer from 'react-dom/server';
import styles from '@components-clean/core/charts/shared/style.module.scss';
import { CHART_HEIGHT_PIXELS, getChartEvents } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import { PopOutPage } from '@components-clean/core/page-toolbar/page-toolbar';
import Tooltip from '@components-clean/core/tooltip';
import { ComboColumnLine, HighchartsWithEvents } from '@interfaces-clean/panels.interface';
import { Permissions } from 'contexts/user-context';
import { UserContext } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { ShowMessage } from '@utils/notification';
import { AllPaths } from '@utils/router/all-paths';

CustomEvents(Highcharts);
export interface HighchartsTooltipElementProps {
    context: Point;
}

interface ComboColumnLineChartProps {
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    panelData: ComboColumnLine;
    showLegend?: boolean;
    TooltipElement?: (props: HighchartsTooltipElementProps) => ReactElement;
    dataviewRef?: string | null;
}
const ComboColumnLineChart = ({
    chartRef,
    panelData,
    showLegend,
    TooltipElement,
    dataviewRef,
}: ComboColumnLineChartProps) => {
    const { series, xCategories, yTitle, yTitleSecondary } = panelData;
    const [open, setOpen] = useState<boolean>(false);
    const [targetElement, setTargetElement] = useState<EventTarget | null>(null);
    const [options, setOptions] = useState<HighchartsWithEvents | null>(null);
    const dataViewRoute = AllPaths.path('cm2-data-views', {
        dataView: dataviewRef,
    });
    const { user } = useContext(UserContext);
    const { t } = useTranslation('cm');

    const popoutDataview = (ragColor: number | undefined, datasetCutDate: string) => {
        if (
            !user?.can?.({
                module: Constants.Modules.CM2,
                page: Constants.PageElements.DataViewsCM2,
                permission: Permissions.Enabled,
                element: Constants.PageElements.DataViewsCM2,
            })
        )
            return;

        if (ragColor !== undefined) {
            PopOutPage(
                user?.organisationKey,
                `${dataViewRoute}?sitescore=${ragColor}&datasetcutdate=${datasetCutDate}`,
            );
        }
    };

    useEffect(() => {
        let tooltip: TooltipOptions = {
            shared: false,
        };

        if (TooltipElement) {
            tooltip = {
                ...tooltip,
                useHTML: true,
                formatter(this: Point) {
                    return ReactDOMServer.renderToString(<TooltipElement context={this} />);
                },
            };
        }

        setOptions({
            exporting: {
                chartOptions: {
                    title: { text: panelData?.plotTitle, style: { fontWeight: 'semi-bold' } },
                },
                scale: 3,
            },
            chart: {
                type: 'column',
                zooming: {
                    type: 'xy',
                },
                height: CHART_HEIGHT_PIXELS,
                events: getChartEvents(false, false, panelData?.plotTitle),
            },

            xAxis: {
                categories: xCategories,
                labels: {
                    style: {
                        textOverflow: 'ellipsis',
                        width: 100,
                    },
                    events: {
                        mouseover(e: MouseEvent) {
                            if (e.target) {
                                setTargetElement(e.target);
                                setOpen(true);
                            }
                        },
                        mouseout() {
                            setTargetElement(null);
                            setOpen(false);
                        },
                    },
                },
            },

            yAxis: [
                {
                    allowDecimals: false,
                    title: {
                        text: yTitle,
                    },
                    opposite: false,
                },
                {
                    title: {
                        text: yTitleSecondary,
                    },
                    opposite: true,
                },
            ],
            colors: [
                styles['rag-colour-red'],
                styles['rag-colour-amber'],
                styles['rag-colour-green'],
                styles['rag-colour-purple'],
            ],
            plotOptions: {
                series: {
                    point: {
                        events: {
                            click(eve) {
                                if (dataviewRef)
                                    popoutDataview(
                                        eve.point.series.options?.custom?.ragColorIndex as number,
                                        eve.point.category as string,
                                    );
                                else if (eve.point.series.options?.custom?.ragColorIndex !== undefined)
                                    ShowMessage({
                                        text: t('componentsClean.panels.noDataviewConfigured'),
                                        type: 'none',
                                    });
                            },
                        },
                    },
                },
                column: {
                    stacking: 'normal',
                    borderRadius: {
                        radius: 0,
                        where: 'end',
                        scope: 'stack',
                    },
                },
                line: {
                    linecap: 'square',
                    color: styles['line-colour'],
                },
            },
            tooltip,
            legend: {
                enabled: showLegend,
            },
            series,
        });
    }, [panelData, showLegend, TooltipElement]);

    if (options === null) return <Loading />;

    return (
        <div
            style={{
                marginTop: 17,
            }}
        >
            <Tooltip anchorElement="pointer" open={open} targetElement={targetElement} position="left">
                <HighchartsReact
                    ref={chartRef}
                    highcharts={Highcharts}
                    constructorType="chart"
                    options={options}
                />{' '}
            </Tooltip>
        </div>
    );
};

export default memo(ComboColumnLineChart);
